import React, { useMemo } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { AdjustedProductType, Product } from "../../../interfaces/DTO/mislaka_xml_models";
import { MimshakPensionService } from "../../../services/mimshakPensionService";
import { formatCurrency, formatHandlerCode } from "../../../utils/namesFormater";
import { formatAdjustedProductType } from "../../../utils/enumFormatter";

interface Props {
  products: Product[];
  /** Smaller text for tight spots (cards inside the meeting picture) */
  compact?: boolean;
}

const WARNING_ORANGE = "#ed6c02";

/**
 * Orange warning shown on "סכום נוכחי קצבה משלמת" when a policy reports a projected retirement amount
 * while its current balance is 0 — the affected policies are listed on hover. Renders nothing otherwise.
 */
const ProjectionWithoutBalanceWarning: React.FC<Props> = ({ products, compact = false }) => {
  const suspiciousPolicies = useMemo(() => MimshakPensionService.getPoliciesWithProjectionWithoutBalance(products), [products]);

  if (suspiciousPolicies.length === 0) return null;

  return (
    <Tooltip
      title={
        <Box sx={{ direction: "rtl", display: "flex", flexDirection: "column", gap: 1 }}>
          {suspiciousPolicies.map((policy) => (
            <Box key={policy.policyNumber}>
              <Typography variant="caption" sx={{ display: "block", fontWeight: 700 }}>
                {[
                  policy.productType != null ? formatAdjustedProductType(policy.productType as AdjustedProductType) : null,
                  policy.handlerCode ? formatHandlerCode(policy.handlerCode) : null,
                  policy.clientName,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
              <Typography variant="caption" sx={{ display: "block" }}>
                פוליסה {policy.policyNumber}
                {policy.updateDate ? ` · עדכון ${policy.updateDate}` : ""}
              </Typography>
              <Typography variant="caption" sx={{ display: "block" }}>
                סכום צפוי בפרישה {formatCurrency(policy.projectedAmount)} · יתרה נוכחית 0
              </Typography>
            </Box>
          ))}
        </Box>
      }
    >
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mt: 0.5, color: WARNING_ORANGE, cursor: "help" }}>
        <WarningAmberIcon sx={{ fontSize: compact ? 15 : 18 }} />
        <Typography sx={{ fontSize: compact ? 11 : 13, fontWeight: 700, color: WARNING_ORANGE }}>
          יש פוליסה עם סכום צפוי בפרישה ללא יתרה נוכחית
        </Typography>
      </Box>
    </Tooltip>
  );
};

export default ProjectionWithoutBalanceWarning;
